import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Space, Tag, Popconfirm, message, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './OptionCatalogManager.module.css';

interface OptionValue {
  id?: string;
  value: string;
  display_label?: string;
  description?: string;
  sequence_order: number;
  is_active: boolean;
}

interface OptionCatalog {
  id: string;
  name: string;
  description?: string;
  category?: string;
  display_label?: string;
  multiple_selection_allowed: boolean;
  values?: OptionValue[];
  created_at: string;
  updated_at?: string;
}

interface OptionCatalogManagerProps {
  visible: boolean;
  onClose: () => void;
  onCatalogSelected?: (catalogId: string) => void;
}

const OptionCatalogManager: React.FC<OptionCatalogManagerProps> = ({
  visible,
  onClose,
  onCatalogSelected
}) => {
  const [catalogs, setCatalogs] = useState<OptionCatalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<OptionCatalog | null>(null);
  const [form] = Form.useForm();

  // Fetch catalogs
  const fetchCatalogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/option-catalogs');
      setCatalogs(response.data || []);
    } catch (error: any) {
      console.error('Error fetching catalogs:', error);
      message.error('Failed to fetch option catalogs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchCatalogs();
    }
  }, [visible, fetchCatalogs]);

  const handleAddCatalog = () => {
    setEditingCatalog(null);
    form.resetFields();
    // Initialize with empty options array to avoid duplicate key issues
    form.setFieldsValue({ options: [] });
    setShowModal(true);
  };

  const handleEditCatalog = (catalog: OptionCatalog) => {
    setEditingCatalog(catalog);
    form.setFieldsValue({
      name: catalog.name,
      description: catalog.description,
      category: catalog.category,
      display_label: catalog.display_label,
      multiple_selection_allowed: catalog.multiple_selection_allowed
    });
    
    // Set initial values for options
    // Don't set 'key' - Ant Design Form.List manages keys automatically
    const formValues = catalog.values?.map((val) => ({
      id: val.id,  // Include ID so backend can match and update existing values
      value: val.value,
      display_label: val.display_label,
      description: val.description,
      sequence_order: val.sequence_order,
      is_active: val.is_active
    })) || [];
    
    form.setFieldsValue({ options: formValues });
    setShowModal(true);
  };

  const handleDeleteCatalog = async (catalogId: string) => {
    try {
      await apiClient.delete(`/option-catalogs/${catalogId}`);
      message.success('Catalog deleted successfully');
      fetchCatalogs();
    } catch (error: any) {
      message.error('Failed to delete catalog: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveCatalog = async () => {
    try {
      const catalogValues = await form.validateFields();
      
      const catalogData = {
        ...catalogValues,
        values: catalogValues.options?.filter((opt: any) => opt.value) || []
      };
      
      // Remove the options key since it's not part of the schema
      delete catalogData.options;

      if (editingCatalog) {
        // Update existing catalog
        await apiClient.put(`/option-catalogs/${editingCatalog.id}`, catalogData);
        message.success('Catalog updated successfully');
      } else {
        // Create new catalog
        const response = await apiClient.post('/option-catalogs', catalogData);
        message.success('Catalog created successfully');
        if (onCatalogSelected) {
          onCatalogSelected(response.data.id);
        }
      }

      setShowModal(false);
      fetchCatalogs();
    } catch (error: any) {
      if (error.errorFields) {
        // Validation errors from antd
        return;
      }
      message.error('Failed to save catalog: ' + (error.response?.data?.detail || error.message));
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150
    },
    {
      title: 'Values',
      key: 'values',
      width: 100,
      render: (record: OptionCatalog) => (
        <Tag color="blue">{record.values?.length || 0} options</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (record: OptionCatalog) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCatalog(record)}
          />
          <Popconfirm
            title="Delete this catalog?"
            onConfirm={() => handleDeleteCatalog(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.optionCatalogManager}>
      <div className={styles.optionCatalogHeader}>
        <h2>Option Catalogs</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCatalog}>
          Create Catalog
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={catalogs}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCatalog ? 'Edit Option Catalog' : 'Create Option Catalog'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
          setEditingCatalog(null);
        }}
        onOk={handleSaveCatalog}
        width={800}
        destroyOnHidden={true}
        afterClose={() => {
          // Ensure form is completely reset after modal closes
          form.resetFields();
          setEditingCatalog(null);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Catalog Name"
            name="name"
            rules={[{ required: true, message: 'Please enter catalog name' }]}
          >
            <Input placeholder="e.g., Status Options, Currency Codes" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea rows={2} placeholder="Describe the purpose of this catalog" />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
          >
            <Input placeholder="Optional category for grouping" />
          </Form.Item>

          <Form.Item
            label="Display Label"
            name="display_label"
          >
            <Input placeholder="Label to display in UI" />
          </Form.Item>

          <Form.Item
            label="Multiple Selection Allowed"
            name="multiple_selection_allowed"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <h3 style={{ marginTop: 24 }}>Options</h3>
          <Form.List name="options">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      name={[field.name, 'value']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="Value" style={{ width: 150 }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'display_label']}
                    >
                      <Input placeholder="Display Label" style={{ width: 150 }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'description']}
                    >
                      <Input placeholder="Description" style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'sequence_order']}
                    >
                      <Input type="number" placeholder="Order" style={{ width: 80 }} />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)}>Remove</Button>
                  </Space>
                ))}
                <Button 
                  type="dashed" 
                  onClick={() => add({
                    value: '',
                    display_label: '',
                    description: '',
                    sequence_order: fields.length,
                    is_active: true
                  })} 
                  block 
                  icon={<PlusOutlined />}
                >
                  Add Option
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default OptionCatalogManager;

